/**
 * C5 — Crew Member chrome (Mate face + status chip)
 *
 * Reads `crew_member_draft` prefs (NOT_BOUND_YET) for the Your-crew panel.
 * Panel status chip is **idle only**. Header variant on chat-ui mounts when a
 * Crew Chat roster is bound (LIVE) and may show thinking/working activity.
 *
 * Preserve LosiAnimated (engine via LosiAvatar). No Muse/Vesper.
 * Metro cannot import this file — mobile mirrors in
 * mobile/lib/crew/member-chrome.ts.
 *
 * Dependencies: member-bind (draft), mates (shape bridge)
 */

import {
  CREW_MEMBER_BIND_STATUS_BOUND,
  CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET,
  type CrewMemberBindStatus,
  type CrewMemberDraft
} from '@/lib/crew/member-bind'
import {
  MATE_BY_ID,
  mateIdToFeedShape,
  type CrewFeedMateShapeId,
  type MateId
} from '@/lib/crew/mates'

/**
 * Closed vocabulary from crew-mate AGENT_STATUSES (subset for Crew chrome).
 * Your-crew draft panel stays idle; chat header / Activity may pass live status.
 */
export const CREW_MEMBER_CHROME_STATUS_VOCAB = [
  'idle',
  'thinking',
  'tooling',
  'waiting',
  'success',
  'error',
  'handoff'
] as const

export type CrewMemberChromeStatusVocab =
  (typeof CREW_MEMBER_CHROME_STATUS_VOCAB)[number]

/** Default / draft-panel status. */
export const CREW_MEMBER_CHROME_STATUS_IDLE = 'idle' as const

/** Live chrome may use the full vocab (C5). */
export type CrewMemberChromeStatus = CrewMemberChromeStatusVocab

/**
 * Visible status chip label — silenced (anti-slop; do not ship idle enum token).
 * Hosts must gate mounts when empty. Engine state still uses STATUS_IDLE.
 */
export const CREW_MEMBER_CHROME_STATUS_LABEL = '' as const

/**
 * Statuses that are live when hosts pass explicit activity (chat header).
 * Draft Your-crew panel still forces idle via resolveCrewMemberChrome().
 */
export const CREW_MEMBER_CHROME_LIVE_STATUSES = [
  'thinking',
  'tooling',
  'waiting',
  'success',
  'error',
  'handoff'
] as const satisfies readonly Exclude<
  CrewMemberChromeStatusVocab,
  typeof CREW_MEMBER_CHROME_STATUS_IDLE
>[]

/** @deprecated alias — prefer CREW_MEMBER_CHROME_LIVE_STATUSES */
export const CREW_MEMBER_CHROME_NOT_LIVE_STATUSES =
  CREW_MEMBER_CHROME_LIVE_STATUSES

/** Animal mates without an engine shape use this LosiAvatar fallback. */
export const CREW_MEMBER_CHROME_FACE_FALLBACK_SHAPE =
  'peta' as const satisfies CrewFeedMateShapeId

/** Region a11y when chrome is visible — short factual label (no scaffold jargon). */
export const CREW_MEMBER_CHROME_REGION_A11Y = 'Mate'

/**
 * Section title on Your crew — silenced (anti-slop; REGION_A11Y covers a11y).
 * Hosts must gate mounts when empty.
 */
export const CREW_MEMBER_CHROME_TITLE = '' as const

/**
 * Honesty — silenced (anti-slop; do not ship C5 / NOT_BOUND_YET jargon).
 * Hosts must gate mounts when empty.
 */
export const CREW_MEMBER_CHROME_HONESTY = '' as const

/**
 * Animal face-fallback invent phrase — silenced (anti-slop).
 * Hosts must gate when empty; engine still uses FACE_FALLBACK_SHAPE.
 */
export const CREW_MEMBER_CHROME_FACE_FALLBACK_LABEL = '' as const

/**
 * Where chrome is mounted today.
 * - your-crew: draft panel (idle only)
 * - chat header: LIVE when a Crew Chat roster is bound (header variant)
 */
export const CREW_MEMBER_CHROME_SURFACE_YOUR_CREW = 'your-crew' as const

export const CREW_MEMBER_CHROME_CHAT_HEADER_MOUNT = 'LIVE' as const

export type CrewMemberChromeChatHeaderMount =
  typeof CREW_MEMBER_CHROME_CHAT_HEADER_MOUNT

/** Live activity for header roster seats (Crew Chat). Draft panel stays idle. */
export type CrewMemberChromeActivity =
  | 'idle'
  | 'thinking'
  | 'working'
  | 'waiting'
  | 'done'
  | 'handoff'

/**
 * Map header activity → LosiAvatar engine state (preserve LosiAnimated).
 * Draft panel always uses STATUS_IDLE — do not call this for unbound chrome.
 */
export function resolveCrewMemberChromeAvatarState(
  activity: CrewMemberChromeActivity | null | undefined
): 'idle' | 'agentThink' | 'agentRun' | 'agentTool' | 'notify' {
  switch (activity) {
    case 'thinking':
      return 'agentThink'
    case 'working':
      return 'agentRun'
    case 'waiting':
      return 'notify'
    case 'handoff':
      return 'agentTool'
    case 'done':
    case 'idle':
    default:
      return 'idle'
  }
}

/**
 * Resolve seat activity for header strip.
 * Thinking/working only from explicit activityByMateId — never from activeMateId
 * alone (selection highlight ≠ generation). Otherwise the bound mate sticks in
 * "thinking" after the turn ends.
 */
export function resolveCrewMemberChromeHeaderSeatStatus(input: {
  mateId: string
  activeMateId?: string | null
  activityByMateId?: Partial<Record<string, CrewMemberChromeActivity>>
}): CrewMemberChromeActivity {
  const explicit = input.activityByMateId?.[input.mateId]
  if (explicit) return explicit
  return CREW_MEMBER_CHROME_STATUS_IDLE
}

/**
 * Closed chrome set (web ↔ mobile lockstep).
 * Prefer named exports at call sites; MIRROR is for parity audits.
 */
export const CREW_MEMBER_CHROME_MIRROR = {
  CREW_MEMBER_CHROME_STATUS_IDLE,
  CREW_MEMBER_CHROME_STATUS_LABEL,
  CREW_MEMBER_CHROME_REGION_A11Y,
  CREW_MEMBER_CHROME_TITLE,
  CREW_MEMBER_CHROME_HONESTY,
  CREW_MEMBER_CHROME_FACE_FALLBACK_LABEL,
  CREW_MEMBER_CHROME_SURFACE_YOUR_CREW,
  CREW_MEMBER_CHROME_CHAT_HEADER_MOUNT,
  CREW_MEMBER_CHROME_FACE_FALLBACK_SHAPE,
  CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET
} as const

/** Resolved chrome model for UI hosts. */
export type CrewMemberChromeModel = {
  mateId: MateId
  /** LosiAvatar shape — animal mates fall back to peta (honest engine face). */
  shape: CrewFeedMateShapeId
  /** Catalog label (Peta / Fox / …). */
  mateLabel: string
  /** User display name when set; else mateLabel. */
  displayLabel: string
  bindStatus: CrewMemberBindStatus
  status: CrewMemberChromeStatus
  statusLabel: typeof CREW_MEMBER_CHROME_STATUS_LABEL
  /** True when mateId has no native engine shape (animal roster). */
  usesFaceFallback: boolean
}

/**
 * Resolve chrome status from explicit activity.
 * Unknown / missing → idle. Draft panel callers should omit raw.
 */
export function resolveCrewMemberChromeStatus(
  raw?: unknown
): CrewMemberChromeStatus {
  if (typeof raw !== 'string') return CREW_MEMBER_CHROME_STATUS_IDLE
  const lower = raw.trim().toLowerCase()
  if (
    (CREW_MEMBER_CHROME_STATUS_VOCAB as readonly string[]).includes(lower)
  ) {
    return lower as CrewMemberChromeStatus
  }
  // Map Crew Chat activity synonyms → vocab
  if (lower === 'working') return 'tooling'
  if (lower === 'done') return 'success'
  return CREW_MEMBER_CHROME_STATUS_IDLE
}

/**
 * Resolve Mate face shape for LosiAvatar from a catalog mateId.
 * Trio shapes map; animals → CREW_MEMBER_CHROME_FACE_FALLBACK_SHAPE.
 */
export function resolveCrewMemberChromeShape(mateId: MateId): {
  shape: CrewFeedMateShapeId
  usesFaceFallback: boolean
} {
  const mapped = mateIdToFeedShape(mateId)
  if (mapped) {
    return { shape: mapped, usesFaceFallback: false }
  }
  return {
    shape: CREW_MEMBER_CHROME_FACE_FALLBACK_SHAPE,
    usesFaceFallback: true
  }
}

/**
 * Build chrome from a crew_member_draft.
 * Your-crew draft panel: pass no activity → idle.
 * Chat header / Activity: pass `activity` for live tooling/handoff.
 * bindStatus mirrors draft (BOUND only with helperId).
 */
export function resolveCrewMemberChrome(
  draft: CrewMemberDraft | null | undefined,
  activity?: unknown
): CrewMemberChromeModel | null {
  if (draft == null) return null

  const mate = MATE_BY_ID.get(draft.mateId)
  const mateLabel = mate?.label ?? draft.mateId
  const trimmedName = draft.displayName.trim()
  const { shape, usesFaceFallback } = resolveCrewMemberChromeShape(draft.mateId)
  const bindStatus: CrewMemberBindStatus =
    draft.bindStatus === CREW_MEMBER_BIND_STATUS_BOUND && draft.helperId
      ? CREW_MEMBER_BIND_STATUS_BOUND
      : CREW_MEMBER_BIND_STATUS_NOT_BOUND_YET

  return {
    mateId: draft.mateId,
    shape,
    mateLabel,
    displayLabel: trimmedName || mateLabel,
    bindStatus,
    status: resolveCrewMemberChromeStatus(activity),
    statusLabel: CREW_MEMBER_CHROME_STATUS_LABEL,
    usesFaceFallback
  }
}
